import { aeriosCompact, aeriosPx } from "./aerios.gen.js";

/**
 * D77 — density, decided 12 August 2026, on measurements taken from THIS
 * application.
 *
 * Density is a property of a **surface**: set once on a region and inherited,
 * never chosen per instance. That is the rule that stops a system acquiring
 * five control heights nobody can justify — which is exactly what had happened
 * here. D77 counted them:
 *
 *   40px  theme `MuiButton.contained`
 *   36px  `compactTabsSx`
 *   32px  `compactControlHeight`
 *   24px  `compactToggleXsSx`
 *   32.08px  the filter widget's toggles, which set no height at all and land
 *            there by arithmetic
 *
 * Two of those are not design decisions. The 24px step exists, by its own
 * comment, so toggles are not the tallest thing in a row beside a `size="small"`
 * MUI Chip — 24px because MUI says so. Naming it converts an exception into a
 * primitive with a test; leaving it out would not remove it from the code, only
 * from review.
 *
 * Three declared heights replace five undeclared ones.
 */
export const density = {
  /** The default. Needs no attribute. Clears WCAG SC 2.5.5 (AAA) at 44px. */
  comfortable: {
    controlHeight: aeriosPx.controlH, // 44
    controlFont: aeriosPx.controlFont, // 16.5
    controlPaddingX: aeriosPx.controlPx, // 24
    controlGap: aeriosPx.controlGap, // 12
    /** Comfortable labels EACH control, above it. */
    fieldLabelGap: aeriosPx.fieldLabelGap, // 8
    groupGap: aeriosPx.groupGap, // 24
  },

  /**
   * Dense working surfaces. Opted into on a container:
   * `<main data-density="compact">`. 32px clears the AA floor with 8px spare.
   *
   * Barred from touch surfaces, first-run flows, destructive confirmations and
   * the marketing site.
   *
   * Compact changes control size, spacing and labelling. It never changes
   * colour, radius, type family or meaning — **if a rule about any of those
   * differs between densities, the rule is wrong, not the density.**
   */
  compact: {
    controlHeight: Number.parseFloat(aeriosCompact.controlH), // 32
    controlFont: Number.parseFloat(aeriosCompact.controlFont), // 13
    controlPaddingX: Number.parseFloat(aeriosCompact.controlPx), // 10
    controlGap: Number.parseFloat(aeriosCompact.controlGap), // 8
    /** Compact labels a GROUP, not each control — hence zero. */
    fieldLabelGap: Number.parseFloat(aeriosCompact.fieldLabelGap), // 0
    groupGap: Number.parseFloat(aeriosCompact.groupGap), // 24
  },

  /**
   * NOT a third density. It is the size a control takes when it sits INSIDE a
   * row of data rather than above it, and it is the same value in both.
   *
   * The test is mechanical: **a control that acts on the row it sits in is
   * inline; a control that acts on everything below it takes the density's
   * height.** A scope toggle beside a row's status chip is inline. A filter
   * that narrows the whole table is not.
   *
   * 24px sits on the WCAG 2.2 SC 2.5.8 floor EXACTLY, so an inline control MUST
   * carry the spacing exception — 24px of clear space between adjacent targets
   * — and is barred from touch surfaces.
   */
  inline: {
    controlHeight: aeriosPx.controlInlineH, // 24
    controlFont: aeriosPx.controlInlineFont, // 12
    controlPaddingX: aeriosPx.controlInlinePx, // 8
  },

  /**
   * WCAG 2.2 SC 2.5.8 *Target Size (Minimum)*, Level AA — the REQUIRED floor,
   * and what the European Accessibility Act requires.
   *
   * D77 corrected this: the guide previously read "44px minimum target", flat.
   * 44px is SC 2.5.5 *Enhanced*, Level AAA. Stating AAA as the floor made every
   * dense control in this app read as a defect when none of them were.
   *
   * If a test asserts this value, it now checks the legal minimum rather than
   * the comfortable default.
   */
  targetMin: aeriosPx.targetMin, // 24
} as const;

export type DensityMode = "comfortable" | "compact";

/**
 * The attribute a region sets to opt in. Read it, never guess it — density is
 * inherited from the surface, and a component that decides its own has already
 * broken the rule.
 */
export const DENSITY_ATTR = "data-density" as const;
